/**
 * Projects — the other shape of work.
 *
 * A course is a book: numbered exercises, a facit to check against, a chapter that owns a range
 * of numbers. A project is none of that. It is a named space with sheets in it — hobby maths, a
 * proof being chewed on, lecture notes — so it has no numbering, no verdicts and nothing to be
 * marked right or wrong against.
 *
 * The one structural difference that matters: PLAN.md §9 says a project is a FOLDER, not a
 * stack. Its sheets are named, and their order is the owner's, not the clock's. That is why the
 * order lives in the project record as an explicit list rather than being derived from
 * `updatedAt` the way the sheet switcher does it.
 *
 * Everything here is pure. The reactive store and the persistence live in `workspace.ts`, the
 * same split `curriculum.ts` has, so this file is exercisable in Vitest without a DOM.
 */

/** A project's colour is one of the four curve hues, exactly as a course's is. */
export type ProjectAccent = 1 | 2 | 3 | 4;

export const PROJECT_ACCENTS: readonly ProjectAccent[] = [1, 2, 3, 4];

export interface ProjectSheet {
  /** The key in the `sheets` store, so the canvas opens it with no indirection. */
  id: string;
  name: string;
  createdAt: number;
}

export interface Project {
  id: string;
  name: string;
  /** One line, shown under the name on the card. May be empty. */
  description: string;
  accent: ProjectAccent;
  pinned: boolean;
  /** Archived, never deleted — PLAN.md §9. An archived project keeps every sheet it had. */
  archived: boolean;
  createdAt: number;
  updatedAt: number;
  /** Ordered. Index 0 is what `/project/:id` opens. */
  sheets: ProjectSheet[];
}

const uid = (prefix: string): string =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Sheet ids are prefixed rather than reusing `sheet-`.
 *
 * The `sheets` store is shared with the scratch sheet and with every task's sheet, and a task's
 * key is `sheet-<chapterId>-<number>`. A distinct prefix means a project sheet can never be
 * confused for one of those by a later migration or by anyone reading the store by hand.
 */
export const newProjectSheet = (name: string, at = Date.now()): ProjectSheet =>
  ({ id: uid('psheet'), name, createdAt: at });

export const newProject = (fields: {
  name: string;
  description?: string;
  accent?: ProjectAccent;
  /** A project always opens onto a sheet, so it is born with one. */
  firstSheet?: string;
}): Project => {
  const now = Date.now();
  return {
    id: uid('project'),
    name: fields.name.trim() || 'Untitled project',
    description: fields.description?.trim() ?? '',
    accent: fields.accent ?? 1,
    pinned: false,
    archived: false,
    createdAt: now,
    updatedAt: now,
    sheets: [newProjectSheet(fields.firstSheet?.trim() || 'Sheet 1', now)],
  };
};

/** Every write goes through here, so nothing can change a project without dating it. */
export const touch = (project: Project, changes: Partial<Project>, at = Date.now()): Project =>
  ({ ...project, ...changes, updatedAt: at });

// ── The folder ─────────────────────────────────────────────────────────────────────────────

export const sheetAt = (project: Project, sheetId: string): number =>
  project.sheets.findIndex(sheet => sheet.id === sheetId);

export const renameSheet = (project: Project, sheetId: string, name: string): Project => {
  const clean = name.trim();
  if (!clean) return project;
  return touch(project, {
    sheets: project.sheets.map(sheet => (sheet.id === sheetId ? { ...sheet, name: clean } : sheet)),
  });
};

/**
 * Move a sheet by `delta` places, clamped.
 *
 * Clamped rather than refused: a reorder control at the end of a list that does nothing is
 * indistinguishable from one that is broken, so the caller disables the button and this stays
 * total.
 */
export const moveSheet = (project: Project, sheetId: string, delta: number): Project => {
  const from = sheetAt(project, sheetId);
  if (from < 0 || delta === 0) return project;
  const to = Math.min(project.sheets.length - 1, Math.max(0, from + delta));
  if (to === from) return project;
  const sheets = [...project.sheets];
  const [moved] = sheets.splice(from, 1);
  sheets.splice(to, 0, moved!);
  return touch(project, { sheets });
};

export const insertSheet = (project: Project, sheet: ProjectSheet, at?: number): Project => {
  const sheets = [...project.sheets];
  const index = at === undefined ? sheets.length : Math.min(sheets.length, Math.max(0, at));
  sheets.splice(index, 0, sheet);
  return touch(project, { sheets });
};

/** Detach without destroying: the caller decides what happens to the stored sheet record. */
export const detachSheet = (
  project: Project, sheetId: string,
): { project: Project; removed: ProjectSheet | null; index: number } => {
  const index = sheetAt(project, sheetId);
  if (index < 0) return { project, removed: null, index: -1 };
  const removed = project.sheets[index]!;
  return {
    project: touch(project, { sheets: project.sheets.filter(sheet => sheet.id !== sheetId) }),
    removed,
    index,
  };
};

/** "Sheet 3", skipping any number already taken so two sheets never share a name by default. */
export const nextSheetName = (project: Project): string => {
  const taken = new Set(project.sheets.map(sheet => sheet.name));
  let n = project.sheets.length + 1;
  while (taken.has(`Sheet ${n}`)) n += 1;
  return `Sheet ${n}`;
};

/** "Trig identities" → "Trig identities copy" → "Trig identities copy 2". */
export const copyName = (name: string, taken: readonly string[]): string => {
  const existing = new Set(taken);
  const base = `${name} copy`;
  if (!existing.has(base)) return base;
  let n = 2;
  while (existing.has(`${base} ${n}`)) n += 1;
  return `${base} ${n}`;
};

/** The sheet to land on after leaving `sheetId` — the next one, or the last if it was last. */
export const neighbourSheet = (project: Project, sheetId: string): ProjectSheet | null => {
  const index = sheetAt(project, sheetId);
  if (index < 0) return project.sheets[0] ?? null;
  return project.sheets[index + 1] ?? project.sheets[index - 1] ?? null;
};

// ── Browsing ───────────────────────────────────────────────────────────────────────────────

/**
 * Fold accents away before matching.
 *
 * The owner types Swedish. "Härledning" must be found by typing `harl`, because on a search
 * field that is what the fingers do first.
 */
const fold = (text: string): string =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export const matchesQuery = (project: Project, query: string): boolean => {
  const needle = fold(query.trim());
  if (!needle) return true;
  return needle
    .split(/\s+/)
    .every(word => fold(`${project.name} ${project.description}`).includes(word));
};

/**
 * Pinned first, then most recently worked on.
 *
 * At fifty projects an alphabetical list is a filing cabinet you have to read. What the owner
 * actually wants is the handful he is inside this week, which is what these two keys are.
 */
export const sortProjects = (projects: readonly Project[]): Project[] =>
  [...projects].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return b.updatedAt - a.updatedAt;
  });

export const browse = (
  projects: readonly Project[],
  options: { query?: string; archived?: boolean } = {},
): Project[] =>
  sortProjects(projects.filter(project =>
    project.archived === (options.archived ?? false)
    && matchesQuery(project, options.query ?? '')));

/** How long ago, in the shortest true form. Shown beside the sheet count on every card. */
export function agoLabel(at: number, now = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} w ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months} mo ago` : `${Math.round(days / 365)} y ago`;
}
